/**
 * SmswiserCampaignDetailPage header (back, country, day picker, tabs, title prefix)
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, ChevronLeft, ChevronRight, ClipboardList } from 'lucide-react';

import { Button, DatePicker, Input, LoadingSpinner } from '@/components/ui';
import { addDaysToIso } from '@/features/smswiser-campaigns/utils/dateFormat';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';

import type { MainTab } from '../types';

interface SmswiserCampaignDetailHeaderProps {
  displayCountryKey: string;
  countryKey: string;
  countryName: string;
  isFetching: boolean;
  mainTab: MainTab;
  workDate: string;
  onWorkDateChange: (date: string) => void;
  onMainTabChange: (tab: MainTab) => void;
  /** When true, hide Prepared drops (stats.phone-only users). */
  hidePreparedTab?: boolean;
  showTitlePrefix: boolean;
  titlePrefixInput: string;
  onTitlePrefixChange: (value: string) => void;
  prefixStatus: 'idle' | 'saving' | 'saved' | 'error';
}

export const SmswiserCampaignDetailHeader: FC<SmswiserCampaignDetailHeaderProps> = ({
  displayCountryKey,
  countryKey,
  countryName,
  isFetching,
  mainTab,
  workDate,
  onWorkDateChange,
  onMainTabChange,
  hidePreparedTab = false,
  showTitlePrefix,
  titlePrefixInput,
  onTitlePrefixChange,
  prefixStatus,
}) => {
  const navigate = useNavigate();
  return (
  <div className="flex flex-col gap-4">
    <div className="flex flex-wrap items-center gap-3">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => navigate('/smswiser')}
        aria-label="Back to countries"
      >
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <span className="text-2xl" aria-hidden>
        {countryToFlagEmoji(displayCountryKey)}
      </span>
      <div className="min-w-0 flex-1">
        <h1 className="flex items-center gap-2 truncate text-2xl font-semibold">
          {countryName}
          {isFetching && (
            <LoadingSpinner size="sm" className="shrink-0 text-muted-foreground" />
          )}
        </h1>
        <p className="truncate text-sm text-muted-foreground">
          {isFetching && displayCountryKey !== countryKey
            ? `Switching to ${countryKey}…`
            : 'Country dashboard — select campaigns to view stats or create drops'}
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Day
          </label>
          <div className="flex h-9 w-[220px] items-center rounded-md border border-input bg-background shadow-sm transition-colors focus-within:ring-2 focus-within:ring-ring">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-l-md rounded-r-none text-muted-foreground"
              onClick={() => onWorkDateChange(addDaysToIso(workDate, -1))}
              aria-label="Previous day"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <DatePicker
              value={workDate}
              onChange={onWorkDateChange}
              ariaLabel="Select work day"
              className="h-9 min-w-0 flex-1 justify-center rounded-none border-x border-border/50 px-2 hover:bg-accent/50"
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-r-md rounded-l-none text-muted-foreground"
              onClick={() => onWorkDateChange(addDaysToIso(workDate, 1))}
              aria-label="Next day"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Tabs
          </label>
          <div className="flex h-9 items-center gap-2 rounded-lg border border-border/60 bg-background p-1">
            <Button
              variant={mainTab === 'stats' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onMainTabChange('stats')}
              className="gap-2"
              aria-label="Statistics"
            >
              <BarChart3 className="h-4 w-4" />
              Statistics
            </Button>
            {!hidePreparedTab ? (
              <Button
                variant={mainTab === 'prepared' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onMainTabChange('prepared')}
                className="gap-2"
                aria-label="Prepared drops"
              >
                <ClipboardList className="h-4 w-4" />
                Prepared drops
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>

    {showTitlePrefix && (
      <div className="flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-1.5">
        <label className="shrink-0 text-xs text-muted-foreground" htmlFor="title-prefix">
          Title prefix
        </label>
        <div className="w-40 shrink-0">
          <Input
            id="title-prefix"
            value={titlePrefixInput}
            onChange={(e) => onTitlePrefixChange(e.target.value)}
            placeholder="e.g. AU_CASINO"
            className="h-7 border-0 bg-transparent px-0 py-1 text-sm focus-visible:ring-0"
            aria-label="Campaign title prefix for prepared drops"
          />
        </div>
        {prefixStatus === 'saving' && <span className="text-[10px] text-muted-foreground">Saving…</span>}
        {prefixStatus === 'saved' && <span className="text-[10px] text-success">Saved</span>}
        {prefixStatus === 'error' && <span className="text-[10px] text-destructive">Failed</span>}
      </div>
    )}
  </div>
  );
};
